// Paneles fijos de la interfaz: barra superior, controles de simulación, monitor serie,
// gráfica, salida de compilación, avisos, divisores y pestañas en celular.
import { getDef } from '../parts/index.js';
import { partProps } from '../connectivity.js';
import { WIRE_COLORS } from '../scene/wires.js';
import { escapeHtml } from './html.js';

const $ = (s) => document.querySelector(s);

export function pinName(app, key) {
  const [partId, ...rest] = key.split(':');
  const pin = rest.join(':');
  const part = app.project.parts.find((p) => p.id === partId);
  if (!part) return key;
  const def = getDef(part.type);
  if (def.kind === 'breadboard') return `${partId} ${pin.toUpperCase()}`;
  if (def.kind === 'board') return /^\d+$/.test(pin) ? 'D' + pin : pin.replace(/\.\d+$/, '');
  const p = def.pins && (typeof def.pins === 'function' ? def.pins(partProps(part)) : def.pins).find((x) => x.name === pin);
  return `${partId} ${p ? p.label : pin}`;
}

export function setupPanels(app, editor) {
  // Nombre y guardado ----------------------------------------------------------------------------
  const nameInput = $('#project-name');
  nameInput.addEventListener('change', () => app.rename(nameInput.value));
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') nameInput.blur();
  });
  // En una tarea el nombre es el de la tarea y no se cambia aquí; en solo lectura, tampoco.
  const title = () => (['work', 'template', 'review', 'error'].includes(app.kind) && app.displayName) || (app.project && app.project.name) || '';
  const syncName = () => {
    if (!app.project) return;
    const inTask = ['work', 'template', 'review', 'error'].includes(app.kind);
    nameInput.value = title();
    nameInput.readOnly = inTask || !!app.readOnly;
    nameInput.setAttribute('aria-label', inTask ? 'Tarea' : 'Nombre del proyecto');
    nameInput.title = inTask ? 'Tarea' : app.readOnly ? 'Solo lectura' : 'Nombre del proyecto (clic para cambiarlo)';
    document.title = `${title()} · Simulador Arduino 3D`;
  };
  app.on('project-loaded', syncName);
  app.on('mode', syncName);
  app.on('read-only', syncName);
  // Qué se guarda y dónde (Mis proyectos, tu entrega, la plantilla, solo en este navegador o nada).
  const saveStatus = $('#save-status');
  app.on('save-state', () => {
    if (!app.project) return;
    const s = app.saveStatus();
    saveStatus.textContent = s.text;
    saveStatus.title = s.title;
    saveStatus.className = 'save-status ' + s.cls;
    document.title = `${title()} · Simulador Arduino 3D`;
  });

  // Menús (Archivo y cuenta; el de la cuenta se pinta después, por eso se escucha en el documento) ----------
  const closeMenus = () =>
    document.querySelectorAll('.menu.open').forEach((m) => {
      m.classList.remove('open');
      m.querySelector('[data-menu]')?.setAttribute('aria-expanded', 'false');
    });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-menu]');
    if (!btn) return closeMenus();
    const menu = btn.closest('.menu');
    const open = !menu.classList.contains('open');
    closeMenus();
    menu.classList.toggle('open', open);
    btn.setAttribute('aria-expanded', String(open));
  });
  app.on('escape', closeMenus);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.querySelector('.menu.open')) closeMenus();
  });

  // Controles de simulación ------------------------------------------------------------------
  const run = $('#btn-run');
  const pause = $('#btn-pause');
  const restart = $('#btn-restart');
  const status = $('#sim-status');
  const speed = $('#sim-speed');
  run.addEventListener('click', () => (app.sim.active || app.sim.state === 'compiling' ? app.sim.stop() : app.sim.start()));
  const mute = $('#btn-mute');
  const speaker = mute.innerHTML;
  const muted = '<svg viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9zm12.6 3 2.7-2.7-1.4-1.4-2.7 2.7-2.7-2.7-1.4 1.4 2.7 2.7-2.7 2.7 1.4 1.4 2.7-2.7 2.7 2.7 1.4-1.4z"/></svg>';
  const setMute = (on) => {
    app.sim.audio.muted = on;
    if (on) app.sim.audio.stopAll();
    mute.innerHTML = on ? muted : speaker;
    mute.title = on ? 'Activar el sonido' : 'Silenciar zumbadores';
    try {
      localStorage.setItem('arduino3d.muted', on ? '1' : '');
    } catch {
      // sin almacenamiento local
    }
  };
  let startMuted = false;
  try {
    startMuted = localStorage.getItem('arduino3d.muted') === '1';
  } catch {
    // sin almacenamiento local
  }
  setMute(startMuted);
  mute.addEventListener('click', () => setMute(!app.sim.audio.muted));
  const reload = $('#btn-reload');
  reload.addEventListener('click', () => app.sim.start());
  app.on('code-changed', (changed) => (reload.hidden = !changed));
  pause.addEventListener('click', () => app.sim.pause());
  restart.addEventListener('click', () => app.sim.restart());
  speed.addEventListener('change', () => app.sim.setSpeed(Number(speed.value)));
  const runLabel = run.querySelector('span');
  const playIcon = '<path d="M8 5v14l11-7z"/>';
  const stopIcon = '<path d="M6 6h12v12H6z"/>';
  app.on('sim', ({ state, message }) => {
    const active = state === 'running' || state === 'paused' || state === 'compiling';
    run.classList.toggle('stop', active);
    run.querySelector('svg').innerHTML = active ? stopIcon : playIcon;
    runLabel.textContent = state === 'compiling' ? 'Compilando…' : active ? 'Detener' : 'Simular';
    run.title = active ? 'Detener la simulación (Ctrl+Enter)' : 'Compilar y simular (Ctrl+Enter)';
    pause.disabled = !(state === 'running' || state === 'paused');
    reload.hidden = !(app.sim.codeChanged && (state === 'running' || state === 'paused'));
    restart.disabled = pause.disabled;
    pause.title = state === 'paused' ? 'Reanudar' : 'Pausar';
    pause.innerHTML = state === 'paused' ? '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>' : '<svg viewBox="0 0 24 24"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>';
    if (state === 'compiling') status.innerHTML = '<span class="dot busy"></span>Compilando…';
    else if (state === 'error') status.innerHTML = `<span style="color:var(--danger)">${escapeHtml(message || 'Error')}</span>`;
    else if (state === 'stopped') status.textContent = '';
  });
  app.on('frame', (f) => {
    if (!f) return;
    const t = f.timeMs / 1000;
    const mm = Math.floor(t / 60);
    const ss = (t % 60).toFixed(1).padStart(4, '0');
    const sp = Math.round((f.speed || 0) * 100);
    status.innerHTML = `<span class="dot${f.paused ? ' paused' : ''}"></span>${String(mm).padStart(2, '0')}:${ss} · ${f.paused ? 'pausa' : sp + '%'}`;
    status.title = 'Tiempo simulado · velocidad respecto del tiempo real';
  });

  // Compilación -------------------------------------------------------------------------------
  const info = $('#compile-info');
  const out = $('#compile-out');
  const diag = $('#diagnostics');
  const badge = $('#output-badge');
  app.on('compile', (res) => {
    out.textContent = res.output || (res.ok ? 'Compilación correcta.' : 'Error de compilación');
    diag.innerHTML = '';
    const errors = (res.diagnostics || []).filter((d) => d.severity === 'error');
    const warnings = (res.diagnostics || []).filter((d) => d.severity === 'warning');
    for (const d of [...errors, ...warnings]) {
      const b = document.createElement('button');
      b.className = 'diag ' + d.severity;
      b.innerHTML = `<span class="sev">${d.severity === 'error' ? 'error' : 'aviso'}</span><span class="loc">${escapeHtml(d.file)}:${d.line}</span><span>${escapeHtml(d.message)}</span>`;
      b.onclick = () => editor.goTo(d.file, d.line, d.col);
      diag.appendChild(b);
    }
    badge.hidden = !errors.length && !warnings.length;
    badge.textContent = String(errors.length || warnings.length);
    badge.classList.toggle('warn', !errors.length);
    if (res.ok) {
      const f = res.flash;
      const r = res.ram;
      info.innerHTML = `<span class="ok">✓ Compilado${res.cached ? ' (sin cambios)' : ` en ${(res.ms / 1000).toFixed(1)} s`}</span>` +
        (f ? `<span class="meter">Flash <i><b style="width:${Math.min(100, (100 * f.used) / f.max)}%"></b></i>${f.used} B</span>` : '') +
        (r ? `<span class="meter">RAM <i><b style="width:${Math.min(100, (100 * r.used) / r.max)}%"></b></i>${r.used} B</span>` : '');
    } else {
      info.innerHTML = `<span class="err">✗ ${errors.length ? `${errors.length} error${errors.length > 1 ? 'es' : ''}` : 'No compiló'}</span>`;
      showTab('output');
      if (errors[0]) editor.goTo(errors[0].file, errors[0].line, errors[0].col);
    }
    if (res.quota) showQuota(res.quota);
  });

  // Tope diario de compilaciones (docs/PLANES.md): cuántas quedan hoy, solo cuando son 10 o menos. Viene en `quota`
  // cuando la compilación contó; si no contó (un ejemplo sin cambios), se queda lo último que se dijo.
  const quota = $('#compile-quota');
  const setQuota = (text, cls = '', title = '') => {
    quota.textContent = text;
    quota.className = 'compile-quota' + (cls ? ' ' + cls : '');
    quota.title = title;
    quota.hidden = !text;
  };
  function showQuota({ remaining: n, max }) {
    if (n > 10) return setQuota('');
    const text = n === 0 ? 'Ya no te quedan compilaciones hoy' : n === 1 ? 'Te queda 1 compilación hoy' : `Te quedan ${n} compilaciones hoy`;
    setQuota(text, n <= 3 ? 'low' : '', app.user
      ? `Con el plan Gratis se compila hasta ${max} veces en 24 horas.`
      : `Sin cuenta se compila hasta ${max} veces al día desde esta red.`);
    // En el teléfono el panel del código no se ve mientras se simula: se avisa también abajo.
    if (!quota.offsetParent) app.toast(text);
  }
  // El 429 lo explica una ventana (client/ui/plan.js); aquí queda dicho que no se compiló.
  app.on('compile-limit', () => setQuota('No se compiló: llegaste al tope de compilaciones', 'limit'));

  // Monitor serie ----------------------------------------------------------------------------------
  const serialOut = $('#serial-out');
  const autoscroll = $('#serial-autoscroll');
  // El texto de ayuda del monitor vacío cambia con la simulación corriendo (ver .serial-out.running en styles.css).
  app.on('sim', ({ state }) => serialOut.classList.toggle('running', state === 'running' || state === 'paused'));
  // Sin cable USB (o sin energía) la computadora no recibe nada de la placa ni le manda nada.
  app.on('frame', (f) => serialOut.classList.toggle('nolink', !!f && f.serialLink === false));
  const decoder = { d: new TextDecoder('utf-8', { fatal: false }) };
  let pendingText = '';
  let scheduled = false;
  const MAX = 120000;
  const flushSerial = () => {
    scheduled = false;
    if (!pendingText) return;
    serialOut.appendChild(document.createTextNode(pendingText));
    pendingText = '';
    if (serialOut.textContent.length > MAX) serialOut.textContent = serialOut.textContent.slice(-MAX * 0.8);
    if (autoscroll.checked) serialOut.scrollTop = serialOut.scrollHeight;
  };
  app.on('serial', (bytes) => {
    const text = decoder.d.decode(new Uint8Array(bytes), { stream: true });
    pendingText += text;
    plotter.feed(text);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flushSerial);
    }
  });
  app.on('serial-reset', () => {
    serialOut.textContent = '';
    decoder.d = new TextDecoder('utf-8', { fatal: false });
    plotter.clear();
  });
  $('#serial-clear').addEventListener('click', () => {
    serialOut.textContent = '';
    plotter.clear();
  });
  $('#serial-form').addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $('#serial-input');
    const eol = $('#serial-eol').value.replace('\\n', '\n').replace('\\r', '\r').replace('\\n', '\n');
    if (!app.sim.active) {
      app.toast('Inicia la simulación para enviar datos al Arduino.');
      return;
    }
    if (serialOut.classList.contains('nolink')) {
      app.toast('La placa no está conectada a la computadora (cable USB desconectado o placa sin energía): no recibe nada.');
      return;
    }
    app.sim.serial(input.value + eol);
    const span = document.createElement('span');
    span.className = 'sent';
    span.textContent = `» ${input.value}\n`;
    serialOut.appendChild(span);
    if (autoscroll.checked) serialOut.scrollTop = serialOut.scrollHeight;
    input.value = '';
  });

  // Pestañas inferiores ---------------------------------------------------------------------------
  function showTab(name) {
    document.querySelectorAll('.bottom-tabs .tab').forEach((t) => t.classList.toggle('active', t.dataset.tab === name));
    document.querySelectorAll('.tab-body').forEach((b) => (b.hidden = b.dataset.body !== name));
    document.querySelectorAll('.tab-actions').forEach((a) => (a.style.display = a.dataset.for === name ? '' : 'none'));
    if (name === 'plotter') plotter.draw();
  }
  document.querySelectorAll('.bottom-tabs .tab').forEach((t) => t.addEventListener('click', () => showTab(t.dataset.tab)));

  // Gráfica serie --------------------------------------------------------------------------------
  const plotter = new Plotter($('#plotter'), $('#plot-legend'));

  // Pista y datos del pin ------------------------------------------------------------------------
  const hint = $('#hint');
  app.on('hint', (html) => {
    hint.innerHTML = html || '';
  });
  const tip = $('#pin-tip');
  let probeKey = null;
  let probeTimer = null;
  app.on('pin-tip', ({ pin, e }) => {
    if (!pin) {
      tip.style.display = 'none';
      probeKey = null;
      clearInterval(probeTimer);
      return;
    }
    const rect = tip.parentElement.getBoundingClientRect();
    tip.style.left = e.clientX - rect.left + 'px';
    tip.style.top = e.clientY - rect.top + 'px';
    const key = pin.part + ':' + pin.pin;
    if (key !== probeKey) {
      probeKey = key;
      const part = app.project.parts.find((p) => p.id === pin.part);
      const def = part && getDef(part.type);
      tip.innerHTML = `<b>${escapeHtml(pin.def.label || pin.pin)}</b><small>${escapeHtml(def && def.kind === 'board' ? def.name : pin.part)}</small><span class="v" id="pin-v"></span>`;
      clearInterval(probeTimer);
      probeTimer = null;
    }
    if (app.sim.active && !probeTimer) {
      app.sim.probe([key]);
      probeTimer = setInterval(() => app.sim.probe([probeKey]), 150);
    }
    tip.style.display = 'block';
  });
  app.on('probe', (msg) => {
    const el = document.getElementById('pin-v');
    const r = msg.results && msg.results[probeKey];
    if (!el || !r) return;
    el.textContent = r.floating ? 'sin conexión' : r.isolated ? 'sin tierra común con el Arduino' : `${r.v.toFixed(2)} V · ${r.value ? 'HIGH' : 'LOW'}`;
  });
  app.on('sim', ({ state }) => {
    if (state !== 'running' && state !== 'paused') {
      clearInterval(probeTimer);
      probeTimer = null;
      const el = document.getElementById('pin-v');
      if (el) el.textContent = '';
    }
  });

  // Avisos -------------------------------------------------------------------------------------------
  const warnBox = $('#warnings');
  let lastWarnKey = '';
  const renderWarnings = (frame) => {
    const items = [];
    if (frame && frame.warnings) {
      const shorts = new Set();
      for (const w of frame.warnings) {
        if (w.code === 'short') {
          const names = w.pins.filter((p) => !/^bb\d*:/.test(p)).map((p) => pinName(app, p));
          const k = names.join(' · ');
          if (!shorts.has(k)) {
            shorts.add(k);
            items.push({ cls: '', html: `<b>Cortocircuito</b>: ${escapeHtml(names.slice(0, 5).join(', ') || 'en la protoboard')}` });
          }
        } else if (w.code === 'pin-current') {
          items.push({ cls: '', html: `<b>${escapeHtml(pinName(app, w.pin))}</b> entrega ${(w.current * 1000).toFixed(0)} mA (máximo 40 mA). Agrega una resistencia.` });
        } else if (w.code === 'overcurrent') {
          items.push({ cls: 'soft', html: `<b>${escapeHtml(w.part)}</b>: ${(w.current * 1000).toFixed(0)} mA es demasiado para un LED. Usa una resistencia de 220 Ω.` });
        } else if (w.message) {
          items.push({ cls: 'soft', html: `<b>${escapeHtml(w.part || '')}</b> ${escapeHtml(w.message)}` });
        }
      }
    }
    if (app.layout) {
      for (const id of app.layout.misaligned) {
        const part = app.project.parts.find((p) => p.id === id);
        if (part && !part._ghost) items.push({ cls: 'soft', html: `<b>${escapeHtml(id)}</b> está sobre la protoboard pero sus patas no caen en los agujeros.` });
      }
    }
    const key = items.map((i) => i.html).join('|');
    if (key === lastWarnKey) return;
    lastWarnKey = key;
    warnBox.innerHTML = items.slice(0, 5).map((i) => `<div class="warning ${i.cls}">${i.html}</div>`).join('');
  };
  app.on('frame', renderWarnings);
  app.on('layout', () => renderWarnings(app.sim.lastFrame && app.sim.active ? app.sim.lastFrame : null));

  // Aviso flotante -----------------------------------------------------------------------------
  const toast = $('#toast');
  let toastTimer = null;
  app.on('toast', ({ msg, error }) => {
    toast.textContent = msg;
    toast.className = 'toast' + (error ? ' error' : '');
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast.hidden = true), error ? 5000 : 2200);
  });

  // Color de cable ---------------------------------------------------------------------------------
  const wc = $('#wire-color');
  for (const c of WIRE_COLORS.slice(0, 8)) {
    const b = document.createElement('button');
    b.style.background = c.hex;
    b.title = `Cables nuevos en ${c.name.toLowerCase()} (GND sale negro y 5 V rojo)`;
    b.onclick = () => {
      app.wireColor = c.id;
      wc.querySelectorAll('button').forEach((x) => x.classList.toggle('active', x === b));
    };
    if (c.id === app.wireColor) b.classList.add('active');
    wc.appendChild(b);
  }

  // Vistas y calidad -------------------------------------------------------------------------------
  document.querySelectorAll('[data-view]').forEach((b) => b.addEventListener('click', () => app.setView(b.dataset.view)));
  const quality = $('#quality');
  quality.value = app.stage.quality;
  quality.addEventListener('change', () => app.stage.setQuality(quality.value));

  // Divisores ---------------------------------------------------------------------------------------
  const root = document.documentElement;
  const saved = (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  if (saved('arduino3d.codeW')) root.style.setProperty('--code-w', saved('arduino3d.codeW'));
  if (saved('arduino3d.bottomH')) root.style.setProperty('--bottom-h', saved('arduino3d.bottomH'));
  dragSplitter($('#split-code'), (e) => {
    const w = Math.min(window.innerWidth - 320, Math.max(220, e.clientX));
    root.style.setProperty('--code-w', w + 'px');
    return ['arduino3d.codeW', w + 'px'];
  });
  dragSplitter($('#split-bottom'), (e) => {
    const h = Math.min(window.innerHeight - 200, Math.max(90, window.innerHeight - e.clientY));
    root.style.setProperty('--bottom-h', h + 'px');
    return ['arduino3d.bottomH', h + 'px'];
  });

  // Pestañas en celular ---------------------------------------------------------------------------
  document.querySelectorAll('.mobile-tabs button').forEach((b) =>
    b.addEventListener('click', () => {
      document.querySelectorAll('.mobile-tabs button').forEach((x) => x.classList.toggle('active', x === b));
      document.querySelectorAll('.pane[data-pane]').forEach((p) => p.classList.toggle('active', p.dataset.pane === b.dataset.pane));
      app.stage.resize();
    }),
  );

  showTab('serial');
  return { showTab };
}

function dragSplitter(el, onMove) {
  let last = null;
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    el.classList.add('dragging');
    el.setPointerCapture(e.pointerId);
    const move = (ev) => {
      last = onMove(ev);
    };
    const up = () => {
      el.classList.remove('dragging');
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      if (last) {
        try {
          localStorage.setItem(last[0], last[1]);
        } catch {
          // sin almacenamiento local
        }
      }
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
  });
}

// escapeHtml vive en html.js (sin dependencias, también la usa el portal); se reexporta para los módulos del editor.
export { escapeHtml };

const PLOT_COLORS = ['#22b8c8', '#f0883e', '#a371f7', '#56d364', '#f85149', '#e3b341', '#79c0ff', '#ff7b72'];

/** Gráfica de valores numéricos recibidos por Serial (como el Serial Plotter del IDE). */
class Plotter {
  constructor(canvas, legend) {
    this.canvas = canvas;
    this.legend = legend;
    this.series = [];
    this.names = [];
    this.buf = '';
    this.max = 500;
    this.pending = false;
    new ResizeObserver(() => this.draw()).observe(canvas);
  }
  clear() {
    this.series = [];
    this.names = [];
    this.buf = '';
    this.draw();
  }
  feed(text) {
    this.buf += text;
    const lines = this.buf.split('\n');
    this.buf = lines.pop();
    if (this.buf.length > 500) this.buf = '';
    for (const line of lines) {
      const tokens = line.trim().split(/[\s,;\t]+/).filter(Boolean);
      const values = [];
      const names = [];
      for (const t of tokens) {
        const m = /^([^:]+):(-?\d+(?:\.\d+)?)$/.exec(t);
        if (m) {
          names.push(m[1]);
          values.push(Number(m[2]));
        } else if (/^-?\d+(\.\d+)?$/.test(t)) {
          names.push(null);
          values.push(Number(t));
        }
      }
      if (!values.length || values.length !== tokens.length) continue;
      values.forEach((v, i) => {
        if (!this.series[i]) this.series[i] = [];
        this.series[i].push(v);
        if (this.series[i].length > this.max) this.series[i].shift();
        if (names[i]) this.names[i] = names[i];
      });
    }
    if (!this.pending && this.canvas.offsetParent) {
      this.pending = true;
      requestAnimationFrame(() => {
        this.pending = false;
        this.draw();
      });
    }
  }
  draw() {
    const c = this.canvas;
    if (!c.offsetParent) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    const h = c.clientHeight;
    c.width = w * dpr;
    c.height = h * dpr;
    const g = c.getContext('2d');
    g.scale(dpr, dpr);
    g.fillStyle = '#10151b';
    g.fillRect(0, 0, w, h);
    const all = this.series.flat();
    if (!all.length) {
      this.legend.innerHTML = 'Imprime números con <code>Serial.println(valor)</code> (o varios separados por coma o espacio, o <code>nombre:valor</code>) para graficarlos.';
      return;
    }
    let lo = Math.min(...all);
    let hi = Math.max(...all);
    if (hi - lo < 1e-9) {
      hi += 1;
      lo -= 1;
    }
    const pad = (hi - lo) * 0.08;
    lo -= pad;
    hi += pad;
    const L = 46;
    g.strokeStyle = '#222b36';
    g.fillStyle = '#6b7889';
    g.font = '11px ui-monospace, monospace';
    g.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = 8 + ((h - 16) * i) / 4;
      g.beginPath();
      g.moveTo(L, y);
      g.lineTo(w - 6, y);
      g.stroke();
      const v = hi - ((hi - lo) * i) / 4;
      g.fillText(Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(Math.abs(v) < 10 ? 2 : 1), 4, y + 4);
    }
    this.series.forEach((s, i) => {
      g.strokeStyle = PLOT_COLORS[i % PLOT_COLORS.length];
      g.lineWidth = 1.6;
      g.beginPath();
      s.forEach((v, j) => {
        const x = L + ((w - 6 - L) * (j + this.max - s.length)) / (this.max - 1);
        const y = 8 + ((h - 16) * (hi - v)) / (hi - lo);
        if (j === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      });
      g.stroke();
    });
    this.legend.innerHTML = this.series
      .map((s, i) => `<span><i style="background:${PLOT_COLORS[i % PLOT_COLORS.length]}"></i>${escapeHtml(this.names[i] || 'valor ' + (i + 1))}: <b>${s[s.length - 1]}</b></span>`)
      .join('');
  }
}
